/**
 * Permission Cache Utility
 *
 * Caches user permissions in sessionStorage to avoid repeated API calls
 * Automatically refreshes when stale or missing
 */

import DataApi from '../api/DataApi';
import jwt_decode from 'jwt-decode';

const CACHE_KEY = 'user_permissions';
const CACHE_KEY_FIELD_PERM = 'user_field_permissions_by_object';
const CACHE_TIMESTAMP_KEY = 'user_permissions_timestamp';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes (match backend cache)

/** Uses console.log so it appears with default DevTools levels (debug/verbose is often hidden). */
const logFieldPermissionsByObject = (map, label) => {
  if (!map || typeof map !== 'object' || Array.isArray(map)) {
    console.log(`[Permission Cache][FLS] ${label}: (invalid or empty)`);
    return;
  }
  const keys = Object.keys(map);
  if (keys.length === 0) {
    console.log(
      `[Permission Cache][FLS] ${label}: no field denial entries (empty map — expected if no FLS denials for this role)`
    );
    return;
  }
  const fieldsByObject = Object.fromEntries(
    keys.map((objectName) => {
      const fields = map[objectName];
      const fieldEntries =
        fields && typeof fields === 'object' && !Array.isArray(fields)
          ? Object.entries(fields).map(([fieldName, flags]) => ({
              field: fieldName,
              flags
            }))
          : [];
      return [objectName, fieldEntries];
    })
  );
  console.log(`[Permission Cache][FLS] ${label}:`, fieldsByObject);
};

/**
 * Loud FLS dump (console.warn) — use when normal logs are easy to miss.
 * Reads sessionStorage `user_field_permissions_by_object` (no API call).
 */
export const logFieldPermissionsSnapshot = (reason = '') => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_FIELD_PERM);
    if (!raw) {
      console.warn(
        `[Permission Cache][FLS] SNAPSHOT — ${reason}\n` +
          `No key "${CACHE_KEY_FIELD_PERM}" in sessionStorage (never fetched as non-admin, or not logged in).`
      );
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      console.warn(`[Permission Cache][FLS] SNAPSHOT — ${reason} (invalid JSON)`, e);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      console.warn(`[Permission Cache][FLS] SNAPSHOT — ${reason} (not an object)`, parsed);
      return;
    }
    const summary = Object.entries(parsed).map(([obj, fields]) => {
      const keys =
        fields && typeof fields === 'object' && !Array.isArray(fields) ? Object.keys(fields) : [];
      return `${obj} → ${keys.length ? keys.join(', ') : '(no fields)'}`;
    });
    console.warn(
      `[Permission Cache][FLS] SNAPSHOT — ${reason}\n` +
        `Sparse field denials by object:\n${summary.length ? summary.map((s) => `  • ${s}`).join('\n') : '  • (empty {} — no FLS denials for this role)'}`,
      '\nraw:',
      parsed
    );
  } catch (e) {
    console.warn(`[Permission Cache][FLS] SNAPSHOT — ${reason}`, e);
  }
};

function resolveFlsFieldsForObject(fieldPermissionsByObject, objectName) {
  if (
    !fieldPermissionsByObject ||
    typeof fieldPermissionsByObject !== 'object' ||
    Array.isArray(fieldPermissionsByObject)
  ) {
    return null;
  }
  const want = String(objectName || '').trim().toLowerCase();
  if (!want) return null;
  for (const k of Object.keys(fieldPermissionsByObject)) {
    if (String(k || '').trim().toLowerCase() === want) {
      const perObj = fieldPermissionsByObject[k];
      return perObj && typeof perObj === 'object' && !Array.isArray(perObj) ? perObj : null;
    }
  }
  return null;
}

/** Lowercased field API names with explicit can_read: false (omit from list/report pickers and grids). */
export function getFlsReadDeniedLowerSet(fieldPermissionsByObject, objectName) {
  const denied = new Set();
  const perObj = resolveFlsFieldsForObject(fieldPermissionsByObject, objectName);
  if (!perObj) return denied;
  for (const fn of Object.keys(perObj)) {
    const meta = perObj[fn];
    if (
      meta &&
      typeof meta === 'object' &&
      Object.prototype.hasOwnProperty.call(meta, 'can_read') &&
      meta.can_read === false
    ) {
      denied.add(String(fn).trim().toLowerCase());
    }
  }
  return denied;
}

/** Lowercased field API names with explicit can_edit: false (read-only in forms). */
export function getFlsEditDeniedLowerSet(fieldPermissionsByObject, objectName) {
  const denied = new Set();
  const perObj = resolveFlsFieldsForObject(fieldPermissionsByObject, objectName);
  if (!perObj) return denied;
  for (const fn of Object.keys(perObj)) {
    const meta = perObj[fn];
    if (
      meta &&
      typeof meta === 'object' &&
      Object.prototype.hasOwnProperty.call(meta, 'can_edit') &&
      meta.can_edit === false
    ) {
      denied.add(String(fn).trim().toLowerCase());
    }
  }
  return denied;
}

/** Field rows with object_name + field_name (reports, etc.). */
export function filterFieldRowsByFlsRead(fieldRows, fieldPermissionsByObject) {
  if (!Array.isArray(fieldRows) || fieldRows.length === 0) return fieldRows;
  return fieldRows.filter((f) => {
    const obj = f.object_name;
    if (!obj) return true;
    const denied = getFlsReadDeniedLowerSet(fieldPermissionsByObject, obj);
    return !denied.has(String(f.field_name || '').toLowerCase());
  });
}

/**
 * Check if user is admin
 */
export const isAdmin = (userInfo) => {
  if (!userInfo) {
    console.log('[isAdmin] No userInfo provided');
    return false;
  }

  const roleName = userInfo.role_name?.toUpperCase();
  const userRole = userInfo.userrole?.toUpperCase();

  console.log('[isAdmin] Checking admin status:', {
    role_name: userInfo.role_name,
    roleName_upper: roleName,
    userrole: userInfo.userrole,
    userRole_upper: userRole
  });

  const isAdminUser = roleName === 'ADMIN' ||
    roleName === 'SUPER_ADMIN' ||
    userRole === 'ADMIN' ||
    userRole === 'SUPER_ADMIN';

  console.log(`[isAdmin] Result: ${isAdminUser}`);
  return isAdminUser;
};

/**
 * Get cached permissions or fetch from API
 */
export const getUserPermissions = async (forceRefresh = false) => {
  try {
    console.log('[Permission Cache] GET PERMISSIONS STARTED');
    console.log(`[Permission Cache] Force refresh: ${forceRefresh}`);

    let userInfoStr = sessionStorage.getItem('userinfo');
    console.log('[Permission Cache] Raw userinfo from sessionStorage:', userInfoStr);

    if (!userInfoStr) {
      console.log('[Permission Cache] No userinfo in sessionStorage, checking token...');
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('[Permission Cache] No token in localStorage');
        return [];
      }

      try {
        const decodedToken = jwt_decode(token);
        console.log('[Permission Cache] Decoded token:', decodedToken);

        sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
        userInfoStr = JSON.stringify(decodedToken);
        console.log('[Permission Cache] Reconstructed userinfo from token');
      } catch (error) {
        console.error('[Permission Cache] Error decoding token:', error);
        return [];
      }
    }

    const userInfo = JSON.parse(userInfoStr);
    console.log('[Permission Cache] Parsed userInfo:', userInfo);
    console.log('[Permission Cache] Role info from userInfo:', {
      role_id: userInfo.role_id,
      role_name: userInfo.role_name,
      userrole: userInfo.userrole
    });

    if (isAdmin(userInfo)) {
      console.log('[Permission Cache] ADMIN user - bypassing permission fetch');
      console.log(
        '[Permission Cache][FLS] Not loaded for admin users (no field-level denial map; all fields allowed).'
      );
      return [];
    }

    const roleId = userInfo.role_id;
    console.log(`[Permission Cache] Extracted role_id: ${roleId}`);

    if (!roleId) {
      console.error('[Permission Cache] User has no role_id in userinfo');
      console.error('[Permission Cache] Full userInfo object:', JSON.stringify(userInfo, null, 2));
      return [];
    }

    const cachedPermissions = sessionStorage.getItem(CACHE_KEY);
    const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
    const cacheAge = cachedTimestamp ? Date.now() - parseInt(cachedTimestamp) : Infinity;

    console.log('[Permission Cache] Cache status:', {
      hasCached: !!cachedPermissions,
      cacheAge: Math.floor(cacheAge / 1000) + 's',
      isStale: cacheAge >= CACHE_TTL
    });

    if (!forceRefresh && cachedPermissions && cacheAge < CACHE_TTL) {
      const hasFlsCache = sessionStorage.getItem(CACHE_KEY_FIELD_PERM) !== null;
      if (hasFlsCache) {
        console.log('[Permission Cache] Using cached permissions');
        const parsed = JSON.parse(cachedPermissions);
        console.log(`[Permission Cache] Cached permissions count: ${parsed.length}`);
        try {
          const flsRaw = sessionStorage.getItem(CACHE_KEY_FIELD_PERM);
          const flsParsed = flsRaw ? JSON.parse(flsRaw) : {};
          const flsNorm =
            flsParsed && typeof flsParsed === 'object' && !Array.isArray(flsParsed)
              ? flsParsed
              : {};
          logFieldPermissionsByObject(flsNorm, 'from sessionStorage cache (same as API shape)');
        } catch (e) {
          console.log('[Permission Cache][FLS] Could not read cached FLS:', e?.message || e);
        }
        return parsed;
      }
      console.log('[Permission Cache] Permissions cached but field FLS missing — refetching');
    }

    console.log(`[Permission Cache] Fetching fresh permissions from API for role ${roleId}`);
    const permissionApi = new DataApi('permissions');
    const response = await permissionApi.get(`/role/${roleId}`);
    console.log('[Permission Cache] API response:', response.data);

    if (response.data.success) {
      const permissions = response.data.data || [];
      const fls = response.data.fieldPermissionsByObject;
      const fieldPermissionsByObject =
        fls && typeof fls === 'object' && !Array.isArray(fls) ? fls : {};

      sessionStorage.setItem(CACHE_KEY, JSON.stringify(permissions));
      sessionStorage.setItem(CACHE_KEY_FIELD_PERM, JSON.stringify(fieldPermissionsByObject));
      sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

      logFieldPermissionsByObject(
        fieldPermissionsByObject,
        'from API (sparse denials: field → { can_read?, can_edit? })'
      );

      console.log(`[Permission Cache] Cached ${permissions.length} permissions`);
      console.log('[Permission Cache] Permissions:', permissions);
      console.log('[Permission Cache] GET PERMISSIONS COMPLETE');
      return permissions;
    }

    console.error('[Permission Cache] API returned unsuccessful response');
    return [];
  } catch (error) {
    console.error('[Permission Cache] Error fetching permissions:', error);
    console.error('[Permission Cache] Error details:', error.message, error.stack);
    return [];
  }
};

/**
 * Check if user has permission for an object and action
 */
export const hasPermission = async (objectName, action = 'read') => {
  try {
    let userInfoStr = sessionStorage.getItem('userinfo');

    if (!userInfoStr) {
      const token = localStorage.getItem('token');
      if (!token) return false;

      try {
        const decodedToken = jwt_decode(token);
        sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
        userInfoStr = JSON.stringify(decodedToken);
      } catch (error) {
        console.error('Error decoding token:', error);
        return false;
      }
    }

    const userInfo = JSON.parse(userInfoStr);

    if (isAdmin(userInfo)) {
      return true;
    }

    const permissions = await getUserPermissions();
    const objectNameLower = (objectName || '').toLowerCase();
    const permission = permissions.find(
      p => (p.object_name || '').toLowerCase() === objectNameLower
    );

    if (!permission || !permission.is_active) {
      return false;
    }

    const { can_read, can_create, can_update, can_delete, view_all, modify_all } = permission;

    switch (action) {
      case 'read':
      case 'view':
        return can_read || view_all || modify_all;
      case 'create':
        return can_create || modify_all;
      case 'update':
      case 'edit':
        return can_update || modify_all;
      case 'delete':
        return can_delete || modify_all;
      default:
        return false;
    }
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Clear permission cache (call after logout or permission changes)
 */
export const clearPermissionCache = () => {
  sessionStorage.removeItem(CACHE_KEY);
  sessionStorage.removeItem(CACHE_KEY_FIELD_PERM);
  sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  console.log('[Permission Cache] Cleared');
};

/**
 * Sparse map object_name → field API name → { can_read?: false, can_edit?: false }.
 * Admins get {} (no denials). Uses same cache TTL as getUserPermissions.
 */
export const getUserFieldPermissionsByObject = async (forceRefresh = false) => {
  try {
    let userInfoStr = sessionStorage.getItem('userinfo');
    if (!userInfoStr) {
      const token = localStorage.getItem('token');
      if (!token) return {};
      try {
        const decodedToken = jwt_decode(token);
        sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
        userInfoStr = JSON.stringify(decodedToken);
      } catch {
        return {};
      }
    }
    const userInfo = JSON.parse(userInfoStr);
    if (isAdmin(userInfo)) {
      console.log(
        '[Permission Cache][FLS] getUserFieldPermissionsByObject: skipped (admin — empty map returned)'
      );
      return {};
    }
    if (!userInfo.role_id) {
      console.log('[Permission Cache][FLS] getUserFieldPermissionsByObject: no role_id — empty map');
      return {};
    }
    await getUserPermissions(forceRefresh);
    const raw = sessionStorage.getItem(CACHE_KEY_FIELD_PERM);
    if (!raw) {
      console.log(
        '[Permission Cache][FLS] getUserFieldPermissionsByObject: no FLS key in sessionStorage after getUserPermissions'
      );
      return {};
    }
    const parsed = JSON.parse(raw);
    const normalized =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    logFieldPermissionsByObject(normalized, 'resolved for getUserFieldPermissionsByObject');
    return normalized;
  } catch (e) {
    console.error('[Permission Cache] getUserFieldPermissionsByObject:', e);
    return {};
  }
};

/**
 * Preload permissions on login (call after successful login)
 */
export const preloadPermissions = async () => {
  try {
    await getUserPermissions(true);
    logFieldPermissionsSnapshot('preloadPermissions() after login');
    console.log('[Permission Cache] Permissions preloaded');
  } catch (error) {
    console.error('[Permission Cache] Error preloading permissions:', error);
  }
};
